import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Modal, Typography } from '@faceup/ui-base'
import { useLayoutEffect, useRef, useState } from 'react'
import { useMedia } from '../../mq'
import HeaderCloseIcon from '../CloseIcon'
import { Header } from '../Header/Header'
import { RecordingStatus, type RecordType } from './types'
import { VoiceRecordingBody } from './VoiceRecordingBody'

const { Title } = Typography
const messages = defineMessages('followUp.components.voiceRecording.voiceRecordingModal', [
  { id: 'title', defaultMessage: 'Voice message' },
] as const)

type Props = {
  isVisible: boolean
  onClose: () => void
  onRecordingSave: (recording: RecordType) => void
}

export const VoiceRecordingModal = ({ isVisible, onClose: _onClose, onRecordingSave }: Props) => {
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>(RecordingStatus.Idle)
  const [recording, setRecording] = useState<RecordType | null>(null)
  const isMdDown = useMedia('mdDown')
  const audioRef = useRef<HTMLAudioElement>(null)

  const showCloseIcon = recordingStatus !== RecordingStatus.Recording

  useLayoutEffect(() => {
    document.body.style.overflow = isMdDown && isVisible ? 'hidden' : 'auto'
  }, [isVisible, isMdDown])

  const onClose = () => {
    setRecording(null)
    setRecordingStatus(RecordingStatus.Idle)
    _onClose()
  }

  const closeIconHandler = () => {
    if (recordingStatus === RecordingStatus.Playing && audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    onClose()
  }

  const handleRecordingSave = (recording: RecordType | null) => {
    if (recording) {
      onRecordingSave(recording)
    }
    onClose()
  }

  if (isMdDown && isVisible) {
    return (
      <div className='fixed top-0 left-0 w-full min-h-screen h-full bg-white z-1000 overflow-x-hidden overflow-y-auto'>
        <Header>
          <Title level={4}>
            <FormattedMessage message={messages.title} />
          </Title>
          {showCloseIcon && <HeaderCloseIcon close={closeIconHandler} />}
        </Header>
        <VoiceRecordingBody
          recording={recording}
          setRecording={setRecording}
          audioRef={audioRef}
          mediaStream={mediaStream}
          setMediaStream={setMediaStream}
          recordingStatus={recordingStatus}
          onRecordingStatusChange={setRecordingStatus}
          onRecordingSave={handleRecordingSave}
        />
      </div>
    )
  }

  return (
    <Modal
      open={isVisible}
      onClose={recordingStatus === RecordingStatus.Recording ? undefined : closeIconHandler}
      width={600}
      title={<FormattedMessage message={messages.title} />}
    >
      <VoiceRecordingBody
        recording={recording}
        setRecording={setRecording}
        audioRef={audioRef}
        mediaStream={mediaStream}
        setMediaStream={setMediaStream}
        recordingStatus={recordingStatus}
        onRecordingStatusChange={setRecordingStatus}
        onRecordingSave={handleRecordingSave}
      />
    </Modal>
  )
}
